import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { ROUTE_STRINGS, TABLE_CONST } from 'Utils/Constants'
import { supabase } from 'Services/supabaseClient'
import { commonMethods } from 'Utils/commonMethods'
import Loader from 'Components/Loader'
import { DialogModal } from 'Components/UIComponents'
import LayoutContainer from 'Components/LayoutComponent'

const InternalCommunities = ({ setLoading }) => {
  const history = useHistory()
  const { t } = useTranslation()
  let rowCount = 8
  let paginationButtons = TABLE_CONST.paginationButtonCount

  const [showErrorDialog, setShowErrorDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {} })
  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [internalCommunitiesData, setInternalCommunitiesData] = useState([])
  const [searchedValue, setSearchedValue] = useState('')

  useEffect(() => {
    fetchInternalCommunitiesData()

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const fetchInternalCommunitiesData = async () => {
    setShowErrorDialog({ ...showErrorDialog, showModal: false })

    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('communities')
        .select(
          `
        id,
        name,
        description,
        created_at,
        community_url,
        required_subscription,
        community_members (
           community_id
        )
      `
        )
        .eq('is_internal', true)

      if (error) {
        setShowErrorDialog({
          ...showErrorDialog,
          showModal: true,
          modalType: 'error',
          message: t('NO_DATA_FOUND'),
          onRetry: () => fetchInternalCommunitiesData(),
          onHide: handleDismiss
        })

        return
      }

      if (data && data.length > 0) {
        const results = data.map((community) => {
          const communityMembers = community.community_members || []

          return {
            communityDate: commonMethods.utcToLocalTime(community.created_at),
            communityName: community.name,
            communityId: community.id,
            description: community.description,
            memberCount: communityMembers.length,
            communityUrl: community.community_url,
            communitySubscription: community.required_subscription
          }
        })

        setInternalCommunitiesData(results)
      }
    } catch (error) {
      setShowErrorDialog({
        ...showErrorDialog,
        showModal: true,
        modalType: 'error',
        message: t('NO_DATA_FOUND'),
        onRetry: () => fetchInternalCommunitiesData(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const deleteCommunityData = async (rowData) => {
    setShowErrorDialog({ ...showErrorDialog, showModal: false })
    setLoading(true)

    try {
      const { data, error, status } = await supabase.from('communities').delete().eq('id', rowData.communityId).select('*').single()

      if (error) {
        setShowErrorDialog({
          ...showErrorDialog,
          showModal: true,
          modalType: 'error',
          message: t('FAILED_TO_DELETE_COMMUNITY'),
          onRetry: () => deleteCommunityData(rowData),
          onHide: handleDismiss
        })
      }

      if (status === 200 && data?.id) {
        setInternalCommunitiesData((prevData) => prevData?.filter((community) => community.communityId !== rowData.communityId))

        setShowErrorDialog({
          ...showErrorDialog,
          showModal: true,
          modalType: 'success',
          message: t('COMMUNITY_DELETED_SUCCESSFULLY'),
          onHide: handleDismiss,
          onSuccess: handleDismiss
        })
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  if (windowSize < TABLE_CONST.maxWidthTab) {
    rowCount = TABLE_CONST.mobileRowCount
    paginationButtons = TABLE_CONST.mobilePaginationButtonCount
  }

  const handleDismiss = () => {
    setShowErrorDialog({ ...showErrorDialog, showModal: false })
  }

  const handleWindowResize = () => {
    setWindowSize(window.innerWidth)
  }

  const handleManageUserClick = (rowData) => {
    history.push({
      pathname: ROUTE_STRINGS.manageInternalUsers,
      state: { communityData: rowData }
    })
  }

  const handleEditClick = (rowData) => {
    history.push({
      pathname: ROUTE_STRINGS.editInternalCommunities,
      state: { communityData: rowData }
    })
  }
  const handleDeleteClick = (rowData) => {
    setShowErrorDialog({
      ...showErrorDialog,
      showModal: true,
      modalType: 'info',
      message: t('COMMUNITY_DELETION_CONFIRMATION'),
      onConfirmation: () => deleteCommunityData(rowData),
      onHide: handleDismiss
    })
  }

  const handleSearchInputChange = (value) => {
    setSearchedValue(value)
  }

  const handleClickAddButton = () => {
    history.push(ROUTE_STRINGS.addInternalCommunities)
  }

  const customActionButton = (rowData) => (
    <div className="custom-action-button-wrapper">
      <Button type="button" icon="pi pi-user-plus" className="custom-add-button" tooltip={t('MANAGE_USER')} tooltipOptions={{ position: 'top' }} onClick={() => handleManageUserClick(rowData)} />
      <Button type="button" icon="pi pi-pencil" className="custom-edit-button" tooltip={t('EDIT')} tooltipOptions={{ position: 'top' }} onClick={() => handleEditClick(rowData)} />
      <Button type="button" icon="pi pi-trash" className="custom-delete-button" tooltip={t('DELETE')} tooltipOptions={{ position: 'top' }} onClick={() => handleDeleteClick(rowData)} />
    </div>
  )

  const internalCommunities = [
    {
      dataField: 'communityName',
      text: t('COMMUNITY_NAME'),
      className: 'column-community-name',
      sortable: true
    },

    {
      dataField: 'communityId',
      text: t('COMMUNITY_ID'),
      className: 'column-community-id',
      sortable: false
    },

    {
      dataField: 'communityDate',
      text: t('CREATED_DATE'),
      className: 'column-created-date',
      sortable: true
    },

    {
      dataField: 'description',
      text: t('COMMUNITY_DESCRIPTION'),
      className: 'column-community-description',
      sortable: false
    },

    {
      dataField: 'memberCount',
      text: t('COMMUNITY_MEMBER'),
      className: 'column-member-count',
      sortable: true
    },
    {
      text: t('ACTIONS'),
      className: 'column-actions',
      body: customActionButton
    }
  ]

  const filteredCommunitiesData = internalCommunitiesData?.filter((item) => item?.communityName?.toLowerCase()?.includes(searchedValue?.toLowerCase()))

  return (
    <LayoutContainer title={t('INTERNAL_COFFEE_COMMUNITY')} breadCrumbsTitle={'internalCommunities'} showAddButton={true} handleAddButtonClick={handleClickAddButton}>
      <div className="internal-communities-wrapper">
        <div className="custom-search-wrapper p-input-icon-left">
          <i className="custom-search-icon pi pi-search" />
          <InputText value={searchedValue} placeholder={t('SEARCH')} className="custom-search-input" onChange={(e) => handleSearchInputChange(e.target.value)} />
        </div>

        <div className="internal-communities-table-wrapper">
          <DataTable value={filteredCommunitiesData} paginator={filteredCommunitiesData?.length} rows={rowCount} pageLinkSize={paginationButtons}>
            {internalCommunities?.map((column, index) => (
              <Column key={index} field={column.dataField} header={column.text} body={column.body} className={column.className} sortable={column.sortable} />
            ))}
          </DataTable>
        </div>

        <DialogModal {...showErrorDialog} onDismiss={handleDismiss} />
      </div>
    </LayoutContainer>
  )
}

export default Loader(InternalCommunities)
